/**
 * En enda sanning för prisspann.
 *
 * Bakgrund: samma tjänst angavs tidigare med olika spann på olika orts- och
 * tjänstesidor (t.ex. företagsfilm 40 000–350 000 kr på en ort och
 * 40 000–350 000 kr på en annan). Svarsmotorer korsläser sidor inom samma
 * domän — motstridiga siffror sänker citeringsfrekvensen.
 *
 * Regler:
 *   - Alla publika prisspann ska komma härifrån, eller matcha ett spann här.
 *   - Ortsvariation uttrycks i text ("inga resepåslag"), aldrig som nytt spann.
 *   - Leveranslöftet skrivs alltid "10–14 dagar".
 *   - `RETIRED_PRICE_CLAIMS` fälls av `bun run qa:pricing`.
 */

export const FILM_PRICING = {
  /** Paraplyspann för projekt (Prime) — används när ingen enskild tjänst pekas ut. */
  primeSpan: "40 000–700 000 kr",
  socialContent: "40 000–120 000 kr",
  foretagsfilm: "40 000–350 000 kr",
  foretagsfilmIntervju: "40 000–90 000 kr",
  foretagsfilmStory: "120 000–350 000 kr",
  reklamfilm: "150 000–450 000 kr",
  brandFilm: "250 000–700 000 kr",
  kundcase: "45 000–260 000 kr",
  employerBranding: "60 000–180 000 kr",
  /** Serie: hero + 6–10 medarbetarklipp. */
  employerBrandingSerie: "170 000–500 000 kr",
  /** Dagskostnad fullt crew (regi, foto, ljud, utrustning). */
  crewDay: "55 000–170 000 kr",
  pulseFrom: "14 995 kr/mån",
} as const;

/**
 * Ett headline-spann per tjänst. `typical` är det spann vi kommunicerar
 * som normalläge, `full` täcker sidans alla nivåer från enklaste paket
 * till största produktion. AI-exporterna (llms.txt, llms-full.txt)
 * genereras härifrån, så maskinläsbara filer aldrig kan säga något annat
 * än sidorna.
 */
export interface ServicePriceHeadline {
  label: string;
  path: string;
  typical: string;
  full: string;
}

export const SERVICE_PRICE_HEADLINES: ServicePriceHeadline[] = [
  { label: "Reklamfilm", path: "/reklamfilm", typical: "150 000–450 000 kr", full: "35 000–900 000 kr" },
  { label: "Företagsfilm", path: "/foretagsfilm", typical: "40 000–350 000 kr", full: "35 000–400 000 kr" },
  { label: "Varumärkesfilm", path: "/varumarkesfilm", typical: "250 000–700 000 kr", full: "150 000–900 000 kr" },
  { label: "Employer branding-film", path: "/employer-branding-film", typical: "60 000–180 000 kr", full: "35 000–220 000 kr" },
  { label: "Rekryteringsfilm", path: "/rekryteringsfilm", typical: "60 000–180 000 kr", full: "20 000–170 000 kr" },
  { label: "Kundcase-film", path: "/kundcase-film", typical: "45 000–260 000 kr", full: "25 000–140 000 kr" },
  { label: "Produktfilm", path: "/produktfilm", typical: "40 000–120 000 kr", full: "25 000–250 000 kr" },
  { label: "Instruktionsfilm", path: "/instruktionsfilm", typical: "40 000–120 000 kr", full: "15 000–240 000 kr" },
  { label: "Utbildningsfilm", path: "/utbildningsfilm", typical: "40 000–120 000 kr", full: "15 000–350 000 kr" },
  { label: "Animerad förklaringsfilm", path: "/animerad-forklaringsfilm", typical: "40 000–120 000 kr", full: "40 000–200 000 kr" },
  { label: "Explainer-video", path: "/explainer-video", typical: "40 000–120 000 kr", full: "40 000–250 000 kr" },
  { label: "Motion graphics", path: "/motion-graphics", typical: "40 000–120 000 kr", full: "35 000–120 000 kr" },
  { label: "3D-animation", path: "/3d-animation", typical: "150 000–450 000 kr", full: "20 000–600 000 kr" },
  { label: "Drönarfilm", path: "/dronarfilm", typical: "16 000–25 000 kr per inspelningsdag", full: "8 000–25 000 kr" },
  { label: "Film för sociala medier", path: "/sociala-medier-film", typical: "40 000–120 000 kr", full: "40 000–120 000 kr" },
  { label: "Livesändning", path: "/livesandning", typical: "25 000–250 000 kr per sändning", full: "25 000–250 000 kr" },
  { label: "Videopodcast", path: "/podcast-video", typical: "15 000–35 000 kr per avsnitt i serie", full: "15 000–70 000 kr" },
];

/**
 * Metodunderlag för prisdatan — det som gör siffrorna citerbara.
 * Uppdatera datum och antal när underlaget räknas om.
 */
export const PRICE_DATA_BASIS = {
  reviewed: "2026-09-07",
  sampleSize: 168,
  sourceSentence:
    "Spannen bygger på Fennys egna offerter och genomförda produktioner (168 uppdrag), inte på branschgissningar. Alla priser är exkl. moms och gäller hela Sverige.",
  reviewer: "Mattias Björk",
} as const;

/** Leveranslöftet — får aldrig skrivas om till andra tal. */
export const DELIVERY_PROMISE = "10–14 dagar";

/**
 * Ortsprissättning: prislistan är rikstäckande. Ortssidor får aldrig ange egna
 * spann — variation uttrycks i text, inte i siffror.
 */
export const NATIONAL_PRICING_NOTE =
  "Samma prislista i hela Sverige — inga ortstillägg och inga resepåslag. Slutpriset styrs av omfattning, inte av var produktionen sker.";

/**
 * Spann som tidigare publicerats och nu är ersatta. Får inte förekomma i src/.
 * Lägg till här när ett spann pensioneras, så fångar QA återfall.
 * Kontrollen är okänslig för mellanslag och typ av tankstreck.
 */
export const RETIRED_PRICE_CLAIMS: string[] = [
  "20 000–150 000 kr",
  "25 000–500 000 kr",
  "40 000–680 000 kr",
  "50 000–700 000 kr",
  "55 000–680 000 kr",
  "240 000–680 000 kr",
  "140 000–430 000 kr",
  "55 000–115 000 kr",
  "60 000–120 000 kr",
  "40 000–115 000 kr",
  "85 000–250 000 kr",
  "90 000–260 000 kr",
  "180 000–520 000 kr",
];
